import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router";
import { ProviderStockLocations, useStockLocations } from "../../api/stocklocations";
import SearchField from "../../interface/shared/searchfield";


export default function StockLocations() {
    return (
        <DashboardLayout>
            <ProviderStockLocations>
                <StockLocationsContent />
            </ProviderStockLocations>
        </DashboardLayout>
    );
}

function StockLocationsContent() {
    const [searchTerm, setSearchTerm] = React.useState("");
    const { stockLocations, errors, loading, fetchStockLocations } = useStockLocations();
    const navigate = useNavigate();

    const filteredStockLocations = stockLocations.filter((stockLocation) => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) {
            return true;
        }
        return (
            stockLocation.name.toLowerCase().includes(query) ||
            stockLocation.warehouse.name.toLowerCase().includes(query)
        );
    }) || [];

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                        Stock Locations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All stock locations in the warehouse.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SearchField
                        placeholder="Search stock locations by name or warehouse"
                        value={searchTerm}
                        onChange={setSearchTerm}
                    />
                </Stack>
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchStockLocations}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Warehouse</TableCell>
                                <TableCell>Levels</TableCell>
                                <TableCell>Columns</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell align="right">
                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                <Skeleton variant="rounded" width={70} height={30} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : filteredStockLocations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                            No stock locations found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredStockLocations.map((stockLocation) => (
                                    <TableRow key={`${stockLocation.name}`}>
                                        <TableCell>{stockLocation.name}</TableCell>
                                        <TableCell>{stockLocation.warehouse.name}</TableCell>
                                        <TableCell>{stockLocation.levels}</TableCell>
                                        <TableCell>{stockLocation.columns}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                onClick={() => navigate(`/locations/${stockLocation.id}`)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
}